import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react'
import { FormattedMessage } from 'react-intl'
import { Container } from '../../BulkEdit/EditInformations/styles'
import _ from 'lodash'
import SpecialHoursRangeInput from './SpecialHoursRangeInput'
import OpenHoursRangeInput from './OpenHoursRangeInput'
import styled from 'styled-components'
import EditableRow from '~/components/EditableRow'
import { FormGroup } from 'reactstrap'
import DateTimePicker from '~/components/DateTimePicker'
import { v4 as uuidv4 } from 'uuid'
import { Calendar } from 'react-feather'
import { format, isEqual } from 'date-fns'

import { EditedFields } from '../index'

const WEEK_DAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
]

const EditHoursForm = ({ formik }) => {
  const {
    editedFields: { hours: editedFields },
    setEditedFields,
  } = useContext(EditedFields)
  const [selectedSpecialDates, setSelectedSpecialDates] = useState([])
  const [specialPeriodsByDay, setSpecialPeriodsByDay] = useState({
    current: null,
    default: null,
  })
  const [regularPeriodsByWeek, setRegularPeriodsByWeek] = useState({
    current: null,
    default: null,
  })
  const specialHoursName = 'specialHours.specialHourPeriods'
  const regularHoursName = 'regularHours.periods'

  const weekDayHours = useMemo(() => {
    if (_.isEmpty(regularPeriodsByWeek.current)) return []
    const currentPeriodsByWeek = _.cloneDeep(regularPeriodsByWeek.current)
    return Object.entries(currentPeriodsByWeek)
  }, [regularPeriodsByWeek])

  const specialHoursDates = useMemo(() => {
    if (_.isEmpty(specialPeriodsByDay.current)) return []
    const currentSpecialPeriods = _.cloneDeep(specialPeriodsByDay.current)
    return Object.entries(currentSpecialPeriods)
  }, [specialPeriodsByDay])

  const handleSwitchChange = switchId => {
    setEditedFields(prevState => ({
      ...prevState,
      hours: {
        ...prevState.hours,
        [switchId]: !editedFields[switchId],
      },
    }))
  }

  const formattedDateFromGoogle = period => {
    if (!period.startDate) return null
    const { day, month, year } = period.startDate

    const date = new Date(year, +month - 1, day)
    return format(date, 'P')
  }

  function getDefaultRegularPeriodHour(weekDay) {
    if (!weekDay) return null

    return {
      openDay: weekDay.toUpperCase(),
      closeDay: weekDay.toUpperCase(),
      openTime: null,
      closeTime: null,
      tempId: uuidv4(),
    }
  }

  function getDefaultSpecialPeriodHour(date) {
    if (!date) return null
    const d = typeof date === 'string' ? new Date(date) : date

    return {
      startDate: {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
      },
      endDate: null,
      openTime: null,
      closeTime: null,
      tempId: uuidv4(),
    }
  }

  const handlePeriodHoursChange = useCallback(
    (name, period, isAdditional) => {
      const isRegularHours = name === regularHoursName

      const updateCurrentPeriod = state => {
        const periodKey = isRegularHours
          ? period.openDay
          : formattedDateFromGoogle(period)

        const periodsFromDay = _.get(state.current, periodKey, [])

        if (isAdditional) {
          if (periodsFromDay.length < 2) {
            periodsFromDay.push(period)
          } else {
            periodsFromDay[1] = period
          }

          return {
            ...state,
            current: {
              ...state.current,
              [periodKey]: periodsFromDay,
            },
          }
        } else {
          periodsFromDay[0] = period

          return {
            ...state,
            current: {
              ...state.current,
              [periodKey]: periodsFromDay,
            },
          }
        }
      }

      if (isRegularHours) {
        setRegularPeriodsByWeek(state => updateCurrentPeriod(state))
      } else {
        setSpecialPeriodsByDay(state => updateCurrentPeriod(state))
      }

      const periods = _.get(formik.values, name)
      const updatedPeriodIndex = periods.findIndex(
        p => p.tempId === period.tempId
      )

      if (updatedPeriodIndex !== -1) {
        formik.setFieldValue(`${name}.[${updatedPeriodIndex}]`, period)
      } else {
        formik.setFieldValue(name, [...periods, period])
      }
    },
    [formik]
  )

  const handleDeleteSpecialPeriodDateFormik = day => {
    const excludePeriodsIds = specialPeriodsByDay.current?.[day]?.map(
      period => period.tempId
    )

    const periods = _.get(formik.values, specialHoursName)
    formik.setFieldValue(
      specialHoursName,
      periods.filter(p => !excludePeriodsIds.includes(p.tempId))
    )
  }

  const handleSelectedSpecialDates = dates => {
    const selectedDates = dates.map(date => format(date, 'P'))
    const currentDates = _.keys(specialPeriodsByDay.current)
    const [diffDate] = _.xor(selectedDates, currentDates)
    const isDeleteDate = _.hasIn(specialPeriodsByDay.current, diffDate)

    if (isDeleteDate) {
      const current = _.omit(specialPeriodsByDay.current, diffDate)
      setSpecialPeriodsByDay(state => ({
        ...state,
        current,
      }))
      handleDeleteSpecialPeriodDateFormik(diffDate)
    } else {
      const specialPeriods = _.get(formik.values, specialHoursName)
      const newSpecialPeriod = getDefaultSpecialPeriodHour(diffDate)
      setSpecialPeriodsByDay(state => ({
        ...state,
        current: {
          ...state.current,
          [diffDate]: [newSpecialPeriod],
        },
      }))

      formik.setFieldValue(specialHoursName, [
        ...specialPeriods,
        newSpecialPeriod,
      ])
    }

    setSelectedSpecialDates(dates)
  }

  const handleExcludeSpecialDate = period => {
    const date = formattedDateFromGoogle(period)
    const current = _.omit(specialPeriodsByDay.current, date)

    setSpecialPeriodsByDay(state => ({
      ...state,
      current,
    }))

    handleDeleteSpecialPeriodDateFormik(date)
    setSelectedSpecialDates(prev =>
      prev.filter(day => !isEqual(day, new Date(date)))
    )
  }

  useEffect(() => {
    const specialPeriods = _.get(formik.initialValues, specialHoursName, [])
    const regularPeriods = _.get(formik.initialValues, regularHoursName, [])

    const periodsByWeek = WEEK_DAYS.reduce((acc, weekDay) => {
      const foundedPeriods = regularPeriods.filter(
        period => period.openDay === weekDay
      )

      if (!foundedPeriods?.length) {
        acc[weekDay] = [
          { ...getDefaultRegularPeriodHour(weekDay), tempId: uuidv4() },
        ]
      } else {
        acc[weekDay] = foundedPeriods
      }
      return acc
    }, {})

    const specialPeriodsGroupedByDay = _.groupBy(
      specialPeriods,
      formattedDateFromGoogle
    )

    setRegularPeriodsByWeek({
      current: periodsByWeek,
      default: _.cloneDeep(periodsByWeek),
    })

    setSpecialPeriodsByDay({
      current: specialPeriodsGroupedByDay,
      default: _.cloneDeep(specialPeriodsGroupedByDay),
    })

    const selectedSpecialDates = _.keys(specialPeriodsGroupedByDay).map(
      day => new Date(day)
    )
    setSelectedSpecialDates(selectedSpecialDates)
  }, [formik.initialValues])

  return (
    <>
      <HoursForm>
        <Container className="pb-4">
          <h4
            style={{ color: '#2C2C2C', fontSize: '1.5rem', fontWeight: '400' }}
          >
            <FormattedMessage id="Edit location hours" />
          </h4>
          <LabelWrapper>
            <div
              style={{
                color: 'rgba(0, 0, 0, 0.85);',
                fontSize: '1rem',
                fontWeight: '500',
              }}
            >
              <FormattedMessage id="Open hours" />
            </div>
            <EditableRow
              switchId="openHoursFields"
              onChange={id => {
                handleSwitchChange(id)
                if (editedFields?.openHoursFields) {
                  const defaultValues = _.get(
                    formik.initialValues,
                    'regularHours'
                  )
                  formik.setFieldValue('regularHours', defaultValues)

                  setRegularPeriodsByWeek(state => ({
                    default: state.default,
                    current: state.default,
                  }))
                }
              }}
              isChecked={!!editedFields?.openHoursFields}
            />
          </LabelWrapper>
          <HoursInputsWrapper>
            {weekDayHours.map(([weekDay, [defaultTime, additionalTime]]) => (
              <OpenHoursRangeInput
                id={`regularHours-${weekDay}`}
                key={`regularHours-${weekDay}`}
                defaultTimeState={[
                  defaultTime || getDefaultRegularPeriodHour(weekDay),
                  period => handlePeriodHoursChange(regularHoursName, period),
                ]}
                aditionalTimeState={[
                  additionalTime || getDefaultRegularPeriodHour(weekDay),
                  period =>
                    handlePeriodHoursChange(regularHoursName, period, true),
                ]}
                day={defaultTime || getDefaultRegularPeriodHour(weekDay)}
                disabled={!editedFields?.openHoursFields}
              />
            ))}
          </HoursInputsWrapper>
        </Container>
        <Container className="pt-2">
          <LabelWrapper>
            <div
              style={{
                color: 'rgba(0, 0, 0, 0.85);',
                fontSize: '1rem',
                fontWeight: '500',
              }}
            >
              <FormattedMessage id="Special hours" />
            </div>
            <EditableRow
              switchId="specialHoursFields"
              onChange={id => {
                handleSwitchChange(id)
              }}
              isChecked={!!editedFields?.specialHoursFields}
            />
          </LabelWrapper>
          <HoursInputsWrapper>
            {specialHoursDates?.map(([day, [defaultTime, additionalTime]]) => (
              <SpecialHoursRangeInput
                id={`specialHours-${day}`}
                key={`specialHours-${day}`}
                defaultTimeState={[
                  defaultTime || getDefaultSpecialPeriodHour(day),
                  specialDate =>
                    handlePeriodHoursChange(specialHoursName, specialDate),
                ]}
                aditionalTimeState={[
                  additionalTime || getDefaultSpecialPeriodHour(day),
                  specialDate =>
                    handlePeriodHoursChange(
                      specialHoursName,
                      specialDate,
                      true
                    ),
                ]}
                day={defaultTime || getDefaultSpecialPeriodHour(day)}
                onExcludeDay={handleExcludeSpecialDate}
                disabled={!editedFields?.specialHoursFields}
              />
            ))}
          </HoursInputsWrapper>
          <FormGroupWrapper>
            <label htmlFor="specialHours" className="mb-1">
              <FormattedMessage id="Add special hours" />:
            </label>
            <div className="d-flex align-items-center" style={{ gap: '1em' }}>
              <Calendar size={20} color="#606060" />
              <DateTimePicker
                style={{ width: '20em' }}
                value={selectedSpecialDates}
                onChange={handleSelectedSpecialDates}
                options={{
                  mode: 'multiple',
                  minDate: 'today',
                }}
                disabled={!editedFields?.specialHoursFields}
              />
            </div>
          </FormGroupWrapper>
        </Container>
      </HoursForm>
    </>
  )
}

const HoursForm = styled.div`
  margin-bottom: 50px;
`

const HoursInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const FormGroupWrapper = styled(FormGroup)`
  max-width: 500px;
`

const LabelWrapper = styled.label`
  display: flex;
  max-width: 700px;
  width: 100%;
  justify-content: space-between;
  pointer-events: none;
`

export default EditHoursForm
